import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Eye, EyeOff, Pencil, Plus } from 'lucide-react';
import { Button, Card, Chip, EmptyState, SkeletonList, toast } from '@/ui';
import { formatIban, maskIban } from '@/lib/iban-format';
import { AccountFormDrawer, type AccountFormValues } from '@/features/accounts/AccountFormDrawer';
import { BANK_LIST, bankDisplayName } from '@/features/accounts/banks';
import { fetchOrganizationAccounts, saveOrganizationAccount } from './api';
import { invalidateAfterAccountMutation } from './invalidation';
import { adminErrorMessage } from './error-message';
import { adminKeys } from './query-keys';
import styles from './Admin.module.css';

/**
 * ============ ISLETMENIN BANKA HESAPLARI (isletme detayi icinde) ============
 *
 * IBAN'lar 2026-08-30'dan beri PLATFORM YONETIMINDE: organizasyon hesaplarini — musteri ve
 * buro ayrimi olmadan — yalniz super_admin tanimlar, cunku hesaplarin bankayla bir onay
 * sureci var. Isletme tarafindaki ekran (features/accounts) artik SALT OKUNUR.
 *
 * NEDEN AYRI BIR MENU OGESI DEGIL: hesap ORGANIZASYONA ait bir kaynak; "once isletmeyi sec,
 * sonra hesaplarini gor" akisi zaten detay sayfasinin kendisi. Duyuru bolumu de ayni
 * gerekceyle metrikler sayfasinin icinde duruyor.
 *
 * FORM YENIDEN KULLANILIYOR: `AccountFormDrawer` isletme tarafindaki dogrulamayi (IBAN
 * bicimi, banka listesi, para birimi) zaten tasiyor. Ikinci bir form, iki farkli IBAN
 * dogrulamasi demekti.
 *
 * SILME YOK: `accountIsActive:false` pasiflestirmedir. Bir IBAN'i gercekten silmek, o hesaba
 * yapilmis gecmis islemlerin izini koparirdi.
 */

type DrawerState = Readonly<{
  session: number;
  editingId: string | undefined;
  values: AccountFormValues;
}>;

const emptyValues = (): AccountFormValues => ({
  bankCode: BANK_LIST[0]?.code ?? '',
  currency: 'USD',
  accountName: '',
  iban: '',
});

export function OrganizationAccountsSection({ organizationId }: { organizationId: string }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const accounts = useQuery({
    queryKey: adminKeys.organizationAccounts(organizationId),
    queryFn: () => fetchOrganizationAccounts(organizationId),
  });

  const [drawer, setDrawer] = useState<DrawerState | null>(null);
  // Artan sayac: form agacini `key` ile bastan kurar. Zaman damgasi DEGIL — `Date.now()`
  // saf olmayan bir cagri ve ayni milisaniyedeki iki acilis ayni anahtari uretebilirdi.
  const drawerSession = useRef(0);
  const nextSession = (): number => {
    drawerSession.current += 1;
    return drawerSession.current;
  };
  // Acikca gosterilmesi istenen satirlar. Kalici DEGIL: yeniden yuklemede hepsi maskelenir.
  const [revealed, setRevealed] = useState<ReadonlySet<string>>(() => new Set());

  const save = useMutation({
    mutationFn: saveOrganizationAccount,
    onSuccess: async () => {
      setDrawer(null);
      await invalidateAfterAccountMutation(queryClient, organizationId);
      toast({ message: t('accounts.saved'), tone: 'success' });
    },
    onError: (error) => {
      toast({ message: adminErrorMessage(error, t), tone: 'danger' });
    },
  });

  const rows = accounts.data ?? [];

  return (
    /* `id="hesaplar"`: isletme listesindeki satirdan dogrudan buraya baglanti veriliyor
       (`/yonetim/isletmeler/:id#hesaplar`). Metrikler sayfasindaki `#duyuru` ile ayni desen:
       bolum kendi menu ogesi olmadan da adreslenebilir olmali. */
    <div id="hesaplar">
    <Card
      padding="md"
      title={t('admin.accounts.sectionTitle')}
      action={
        <Button
          size="sm"
          iconLeft={<Plus size={16} aria-hidden="true" />}
          onClick={() => {
            setDrawer({ session: nextSession(), editingId: undefined, values: emptyValues() });
          }}
        >
          {t('accounts.addAccount')}
        </Button>
      }
    >
      <p className={styles.muted}>{t('admin.accounts.sectionHint')}</p>

      {accounts.isPending ? <SkeletonList rows={2} /> : null}

      {accounts.isError ? (
        <EmptyState
          tone="error"
          title={t('admin.accounts.loadFailed')}
          description={adminErrorMessage(accounts.error, t)}
        />
      ) : null}

      {accounts.isSuccess && rows.length === 0 ? (
        // IBAN'i olmayan bir isletmede musteri TALEP ACAMAZ (hesap secimi zorunlu):
        // bos durum bunu soylemeli, yoksa eksiklik fark edilmez.
        <EmptyState title={t('admin.accounts.empty')} description={t('admin.accounts.emptyHint')} />
      ) : null}

      {rows.length > 0 ? (
        <ul className={styles.rowList}>
          {rows.map((account) => {
            const shown = revealed.has(account.id);
            return (
              <li key={account.id} className={styles.row}>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>
                    {bankDisplayName(account.accountCode)} · {account.accountCurrency}
                  </span>
                  <span className={styles.detailValue}>{account.accountName}</span>
                  <span className={styles.mono}>
                    {shown ? formatIban(account.accountIbanCode) : maskIban(account.accountIbanCode)}
                  </span>
                </div>
                <div className={styles.badges}>
                  {account.isActive ? null : (
                    <Chip size="sm" tone="warning">
                      {t('admin.accounts.inactive')}
                    </Chip>
                  )}
                </div>
                <div className={styles.actionRow}>
                  <Button
                    tone="ghost"
                    size="sm"
                    iconLeft={shown ? <EyeOff size={16} /> : <Eye size={16} />}
                    onClick={() => {
                      setRevealed((current) => {
                        const next = new Set(current);
                        if (next.has(account.id)) next.delete(account.id);
                        else next.add(account.id);
                        return next;
                      });
                    }}
                  >
                    {shown ? t('accounts.hideIban') : t('accounts.showIban')}
                  </Button>
                  <Button
                    tone="neutral"
                    size="sm"
                    iconLeft={<Pencil size={16} aria-hidden="true" />}
                    onClick={() => {
                      setDrawer({
                        session: nextSession(),
                        editingId: account.id,
                        values: {
                          bankCode: account.accountCode,
                          currency: account.accountCurrency,
                          accountName: account.accountName,
                          // Formda bosluklu gosterilir; gonderimde normalize edilir.
                          iban: formatIban(account.accountIbanCode),
                        },
                      });
                    }}
                  >
                    {t('accounts.editAccount')}
                  </Button>
                  {account.isActive ? (
                    <Button
                      tone="ghost"
                      size="sm"
                      loading={save.isPending}
                      onClick={() => {
                        // Pasiflestirme: silme ucu yok ve olmamali (gecmis islemlerin izi).
                        save.mutate({
                          id: account.id,
                          organizationId,
                          accountName: account.accountName,
                          accountCode: account.accountCode,
                          accountCurrency: account.accountCurrency,
                          accountIbanCode: account.accountIbanCode,
                          accountIsActive: false,
                        });
                      }}
                    >
                      {t('admin.accounts.deactivate')}
                    </Button>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      ) : null}

      {drawer ? (
        <AccountFormDrawer
          key={drawer.session}
          open
          onClose={() => {
            setDrawer(null);
          }}
          editingId={drawer.editingId}
          initialValues={drawer.values}
          busy={save.isPending}
          onSubmit={(input) => {
            save.mutate({
              ...(drawer.editingId === undefined ? {} : { id: drawer.editingId }),
              organizationId,
              accountName: input.accountName,
              accountCode: input.bankCode,
              accountLabel: input.bankLabel,
              accountCurrency: input.currency,
              accountIbanCode: input.iban,
              accountType: 'bank',
            });
          }}
        />
      ) : null}
    </Card>
    </div>
  );
}
